import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITDashboard, getITVisits, getITAssets } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Desktop, Wrench, CalendarCheck, CurrencyDollar, ArrowRight, Warning, Ticket, Package, Receipt,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getITErrorMessage } from '../../lib/itErrors';
import { assetDisplayName, assetStatusColor } from '../../components/it-assets';
import { visitStatusColor } from '../../components/it-visits';

const RECENT_LIMIT = 5;
const toList = (data) => (Array.isArray(data) ? data : (data?.items || []));

function StatCard({ icon: Icon, title, value, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-[#1a1d23] border border-gray-800 rounded-lg p-5 hover:border-[#3AB8D6]/50 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon size={22} weight="bold" /></div>
        <ArrowRight size={16} className="text-gray-500" />
      </div>
      <div className="text-2xl font-bold text-white">{value ?? 0}</div>
      <div className="text-sm text-gray-400 mt-1">{title}</div>
    </button>
  );
}

export default function ITDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [recentAssets, setRecentAssets] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      setErrorMessage('');
      const [summary, visits, assets] = await Promise.all([
        getITDashboard(),
        getITVisits().catch(() => ({ data: [] })),
        getITAssets().catch(() => ({ data: [] })),
      ]);
      setData(summary.data);
      setRecentVisits(toList(visits.data).slice(0, RECENT_LIMIT));
      const assetList = toList(assets.data);
      setRecentAssets(assetList.slice(0, RECENT_LIMIT));
      const counts = assetList.reduce((acc, asset) => {
        const key = asset.category || 'other';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      setByCategory(Object.entries(counts).sort((a, b) => b[1] - a[1]));
    } catch (err) {
      const message = getITErrorMessage(err, 'Error al cargar el dashboard de IT');
      setData(null);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E2B05E]"></div>
        </div>
      </Layout>
    );
  }

  if (errorMessage) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl rounded-2xl border border-[#2A3448] bg-[#151B26] p-6 text-center">
          <Warning size={32} className="mx-auto text-yellow-400" />
          <h2 className="mt-2 text-xl font-semibold text-white">No se pudo cargar el dashboard IT</h2>
          <p className="mt-2 text-sm text-gray-400">{errorMessage}</p>
          <Button onClick={loadDashboard} className="mt-5 bg-[#E2B05E] hover:bg-[#c9993f] text-black">Reintentar</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wrench className="text-[#E2B05E]" size={28} /> Operaciones IT
          </h1>
          <p className="text-gray-400 text-sm mt-1">Resumen de activos, visitas, tickets y mantenimiento</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Desktop} title="Activos" value={data?.assets_total} color="bg-[#3AB8D6]/20 text-[#3AB8D6]" onClick={() => navigate('/it/assets')} />
          <StatCard icon={CalendarCheck} title="Visitas abiertas" value={data?.visits_open} color="bg-[#E2B05E]/20 text-[#E2B05E]" onClick={() => navigate('/it/visits')} />
          <StatCard icon={Ticket} title="Tickets abiertos" value={data?.tickets_open} color="bg-purple-500/20 text-purple-400" onClick={() => navigate('/it/tickets')} />
          <StatCard icon={Wrench} title="Mantenimientos pendientes" value={data?.pending_maintenance} color="bg-green-500/20 text-green-400" onClick={() => navigate('/it/maintenance')} />
          <StatCard icon={CurrencyDollar} title="Cotizaciones pendientes" value={data?.quotes_pending} color="bg-blue-500/20 text-blue-400" onClick={() => navigate('/quotations')} />
          <StatCard icon={Receipt} title="Pagos pendientes" value={data?.pending_payments} color="bg-orange-500/20 text-orange-400" onClick={() => navigate('/payments')} />
          <StatCard icon={Package} title="Repuestos con poco stock" value={data?.low_stock_parts} color="bg-red-500/20 text-red-400" onClick={() => navigate('/it/spare-parts')} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#E2B05E] font-semibold">Visitas recientes</h3>
              <Button size="sm" variant="ghost" onClick={() => navigate('/it/visits')} className="text-[#3AB8D6] text-xs">Ver todas</Button>
            </div>
            {recentVisits.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">Sin visitas registradas</p>
            ) : (
              <div className="space-y-2">
                {recentVisits.map((visit) => (
                  <div
                    key={visit._id}
                    onClick={() => navigate(`/it/visits/${visit._id}`)}
                    className="bg-[#12141a] rounded-lg p-3 cursor-pointer hover:bg-[#1e2028]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-medium">{visit.visit_number} · {visit.title}</span>
                      <Badge className={visitStatusColor(visit.status)}>{(visit.status || '').replace(/_/g, ' ')}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {visit.client_name}{visit.scheduled_for ? ` · ${visit.scheduled_for.replace('T', ' ').slice(0, 16)}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#E2B05E] font-semibold">Activos recientes</h3>
              <Button size="sm" variant="ghost" onClick={() => navigate('/it/assets')} className="text-[#3AB8D6] text-xs">Ver todos</Button>
            </div>
            {recentAssets.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">Sin activos registrados</p>
            ) : (
              <div className="space-y-2">
                {recentAssets.map((asset) => (
                  <div
                    key={asset._id}
                    onClick={() => navigate(`/it/assets/${asset._id}`)}
                    className="bg-[#12141a] rounded-lg p-3 cursor-pointer hover:bg-[#1e2028]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-medium">{assetDisplayName(asset)}</span>
                      <Badge className={assetStatusColor(asset.status)}>{(asset.status || '').replace(/_/g, ' ')}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{asset.client_name}{asset.tag ? ` · ${asset.tag}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {byCategory.length > 0 && (
          <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
            <h3 className="text-[#E2B05E] font-semibold mb-3">Activos por categoría</h3>
            <div className="flex flex-wrap gap-3">
              {byCategory.map(([category, count]) => (
                <div key={category} className="bg-[#12141a] rounded-lg px-4 py-2 text-center">
                  <div className="text-lg font-bold text-white">{count}</div>
                  <div className="text-xs text-gray-400 capitalize">{category.replace(/[_-]/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
